import { FC, useState } from "react"
import { isAxiosError } from "axios"
import { Button, SimpleModal } from "@workspace/ui"
import { profileServices } from "@workspace/shared"
import { BLUE_BUTTON, BLUE_STYLE, GHOST_BUTTON } from "./modal-buttons"
import type { DashboardTileDef } from "./tiles"

type RequestAccessModalProps = {
  /** The greyed-out app the coach tapped; null keeps the dialog closed. */
  tile: DashboardTileDef | null
  onClose: () => void
}

/**
 * "Request Access" for an app the team has no subscription for (client
 * 2026-11-04, as in-game). Sending it emails sales@modernfootball.com with
 * the team, the app and who asked — the API reads who asked from the
 * account, so nothing here can claim to be someone else.
 */
export const RequestAccessModal: FC<RequestAccessModalProps> = ({ tile, onClose }) => {
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle")
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    if (status === "sending") return
    setStatus("idle")
    setError(null)
    onClose()
  }

  const send = async () => {
    if (!tile) return
    setStatus("sending")
    setError(null)
    try {
      await profileServices.onRequestAccess(tile.product)
      setStatus("sent")
    } catch (e) {
      const message = isAxiosError(e) ? (e.response?.data as { message?: unknown } | undefined)?.message : null
      setError(
        typeof message === "string" && message
          ? message
          : "Your request didn't go through. Check your connection and try again.",
      )
      setStatus("idle")
    }
  }

  const sent = status === "sent"
  return (
    <SimpleModal
      isOpen={Boolean(tile)}
      onClose={close}
      title={sent ? "Request Sent" : "Request Access"}
      showCloseButton={status !== "sending"}
      footer={
        <div className="flex justify-end gap-2">
          {sent ? (
            <Button variant="default" onClick={close} className={BLUE_BUTTON} style={BLUE_STYLE}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={close} disabled={status === "sending"} className={GHOST_BUTTON}>
                Not Now
              </Button>
              <Button
                variant="default"
                onClick={() => void send()}
                disabled={status === "sending"}
                className={BLUE_BUTTON}
                style={BLUE_STYLE}
              >
                {status === "sending" ? "Sending..." : "Request Access"}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="w-full text-left">
        {sent ? (
          <p className="text-muted-foreground text-sm">
            Thanks! Our team has your request for <strong>{tile?.title}</strong> and will be in touch to help you get
            started.
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">
            This feature isn&rsquo;t currently available for your account. Request access and our team will help you get
            started.
          </p>
        )}
        {error ? (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </SimpleModal>
  )
}
